import { useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  RotateCcw,
  Pencil,
  Share2,
  Check,
  ExternalLink,
  GitCompareArrows,
  Award,
  Weight,
  Gauge,
  ArrowUpRight,
  CircleDot,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { addUtmParams } from "@/utils/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sessionId = "",
    recommendations = [],
    generalAdvice = "",
    productsFound = 0,
    answers = {},
  } = location.state || {};

  const [compareItems, setCompareItems] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [copied, setCopied] = useState(false);

  const trackAction = useCallback((actionType, productName, productUrl, metadata) => {
    if (!sessionId) return;
    axios.post(`${API}/track/action`, {
      session_id: sessionId,
      action_type: actionType,
      product_name: productName || null,
      product_url: productUrl || null,
      metadata: metadata || null,
    }).catch(() => {});
  }, [sessionId, API]);

  if (!recommendations.length && !sessionId) {
    return (
      <div className="min-h-screen bg-[#03170D] flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 text-center max-w-md">
          <h2 className="text-2xl text-white mb-4">No Results Found</h2>
          <p className="text-[#A3B8AA] mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            Start the questionnaire to get personalized shaft recommendations.
          </p>
          <Button
            data-testid="start-over-empty-btn"
            onClick={() => navigate("/questionnaire")}
            className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full px-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Start Questionnaire
          </Button>
        </div>
      </div>
    );
  }

  const shareLink = `${window.location.origin}/results/${sessionId}`;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      trackAction("share", null, shareLink);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard API may fail in non-secure contexts
    }
  };

  const toggleCompare = (rec) => {
    setCompareItems((prev) => {
      const exists = prev.find((r) => r.rank === rec.rank);
      if (exists) return prev.filter((r) => r.rank !== rec.rank);
      if (prev.length >= 2) return [prev[1], rec];
      return [...prev, rec];
    });
  };

  const specIcons = {
    flex: <Gauge className="h-4 w-4" />,
    weight: <Weight className="h-4 w-4" />,
    torque: <CircleDot className="h-4 w-4" />,
    kick_point: <ArrowUpRight className="h-4 w-4" />,
    material: <CircleDot className="h-4 w-4" />,
  };

  return (
    <div className="min-h-screen bg-[#03170D] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <p
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#D4AF37] mb-3"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Your Personalized Recommendations
          </p>
          <h1 data-testid="results-title" className="text-4xl sm:text-5xl tracking-tight leading-none font-medium text-white mb-4">
            Top Shaft Picks
          </h1>
          {generalAdvice && (
            <p
              data-testid="general-advice"
              className="text-base text-[#A3B8AA] max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {generalAdvice}
            </p>
          )}
          <p className="text-sm text-[#647D6C] mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            {productsFound} products analyzed from grips4less.com
          </p>
        </motion.div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          <Button
            data-testid="start-over-btn"
            variant="outline"
            onClick={() => navigate("/questionnaire")}
            className="border-[#1A4026] text-[#A3B8AA] hover:text-white hover:border-[#D4AF37] bg-transparent rounded-full"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Start Over
          </Button>
          <Button
            data-testid="refine-answers-btn"
            variant="outline"
            onClick={() => navigate("/questionnaire", { state: { prefill: answers } })}
            className="border-[#1A4026] text-[#A3B8AA] hover:text-white hover:border-[#D4AF37] bg-transparent rounded-full"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Refine My Answers
          </Button>
          <Button
            data-testid="share-btn"
            variant="outline"
            onClick={handleShare}
            className="border-[#1A4026] text-[#A3B8AA] hover:text-white hover:border-[#D4AF37] bg-transparent rounded-full"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {copied ? <Check className="mr-2 h-4 w-4 text-green-400" /> : <Share2 className="mr-2 h-4 w-4" />}
            {copied ? "Link Copied!" : "Share Results"}
          </Button>
          {compareItems.length === 2 && (
            <Button
              data-testid="compare-btn"
              onClick={() => {
                setShowCompare(true);
                trackAction("compare", null, null, {
                  items: compareItems.map(c => c.product_name),
                });
              }}
              className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <GitCompareArrows className="mr-2 h-4 w-4" />
              Compare Selected
            </Button>
          )}
        </div>

        {/* Recommendation Cards */}
        <div className="space-y-6 stagger-children">
          {recommendations.map((rec, idx) => {
            const isBest = idx === 0;
            const isCompared = compareItems.some((c) => c.rank === rec.rank);
            return (
              <motion.div
                key={rec.rank || idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`recommendation-card-${idx}`}
                className={`rounded-2xl p-6 sm:p-8 transition-all ${
                  isBest
                    ? "gold-glow bg-[#0A2716]"
                    : "glass-card"
                }`}
              >
                <div className="flex flex-col sm:flex-row gap-6">
                  {/* Image */}
                  {rec.image && (
                    <div className="flex-shrink-0">
                      <img
                        src={rec.image}
                        alt={rec.product_name}
                        className="w-full sm:w-32 h-32 object-contain rounded-xl bg-white/5 p-2"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start gap-2 mb-3">
                      {isBest && (
                        <Badge
                          data-testid="best-match-badge"
                          className="bg-[#D4AF37] text-[#03170D] font-semibold hover:bg-[#D4AF37]"
                        >
                          <Award className="mr-1 h-3 w-3" />
                          Best Match
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className="border-[#1A4026] text-[#647D6C]"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        #{rec.rank || idx + 1}
                      </Badge>
                    </div>

                    <h3
                      data-testid={`rec-name-${idx}`}
                      className="text-xl sm:text-2xl font-medium text-white mb-1"
                    >
                      {rec.product_name}
                    </h3>
                    <p className="text-sm text-[#647D6C] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {rec.brand}
                    </p>
                    <p
                      data-testid={`rec-price-${idx}`}
                      className="text-lg font-semibold text-[#D4AF37] mb-4"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {rec.price}
                    </p>

                    {/* Specs */}
                    {rec.specs && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {Object.entries(rec.specs).map(
                          ([key, val]) =>
                            val && (
                              <span
                                key={key}
                                data-testid={`spec-${key}-${idx}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[rgba(26,64,38,0.6)] text-sm text-[#A3B8AA] border border-[#1A4026]"
                                style={{ fontFamily: "Outfit, sans-serif" }}
                              >
                                {specIcons[key] || null}
                                <span className="capitalize">{key.replace("_", " ")}:</span>
                                <span className="text-white">{val}</span>
                              </span>
                            )
                        )}
                      </div>
                    )}

                    {/* Explanation */}
                    {rec.explanation && (
                      <p
                        data-testid={`rec-explanation-${idx}`}
                        className="text-base text-[#A3B8AA] leading-relaxed mb-3"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {rec.explanation}
                      </p>
                    )}

                    {/* Review */}
                    {rec.review_snippet && (
                      <div className="bg-[rgba(10,39,22,0.4)] rounded-lg p-3 mb-4 border-l-2 border-[#D4AF37]">
                        <p
                          className="text-sm italic text-[#647D6C]"
                          style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                          &ldquo;{rec.review_snippet}&rdquo;
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3">
                      {rec.url && (
                        <a href={addUtmParams(rec.url, rec.product_name)} target="_blank" rel="noopener noreferrer" onClick={() => trackAction("shop_now_click", rec.product_name, rec.url)}>
                          <Button
                            data-testid={`shop-now-btn-${idx}`}
                            className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full transition-all hover:-translate-y-0.5"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                          >
                            Shop Now
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                      )}
                      <Button
                        data-testid={`compare-toggle-btn-${idx}`}
                        variant="outline"
                        onClick={() => toggleCompare(rec)}
                        className={`rounded-full border-[#1A4026] transition-all ${
                          isCompared
                            ? "border-[#D4AF37] text-[#D4AF37] bg-[rgba(212,175,55,0.1)]"
                            : "text-[#A3B8AA] hover:text-white hover:border-[#D4AF37] bg-transparent"
                        }`}
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        <GitCompareArrows className="mr-2 h-4 w-4" />
                        {isCompared ? "Selected" : "Compare"}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Comparison Dialog */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="bg-[#0A2716] border-[#1A4026] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">Side-by-Side Comparison</DialogTitle>
            <DialogDescription className="text-[#647D6C]">
              Compare two shaft recommendations head to head
            </DialogDescription>
          </DialogHeader>
          {compareItems.length === 2 && (
            <div className="grid grid-cols-2 gap-6 mt-4">
              {compareItems.map((rec, i) => (
                <div key={rec.rank || rec.product_name || i} className="space-y-4">
                  <h3 className="text-lg font-medium text-white">{rec.product_name}</h3>
                  <p className="text-sm text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>{rec.brand}</p>
                  <p className="text-[#D4AF37] font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{rec.price}</p>
                  {rec.specs && (
                    <div className="space-y-2">
                      {Object.entries(rec.specs).map(([k, v]) =>
                        v ? (
                          <div key={k} className="flex justify-between text-sm border-b border-[#1A4026] pb-2">
                            <span className="text-[#647D6C] capitalize" style={{ fontFamily: "Outfit, sans-serif" }}>{k.replace("_", " ")}</span>
                            <span className="text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{v}</span>
                          </div>
                        ) : null
                      )}
                    </div>
                  )}
                  <p className="text-sm text-[#A3B8AA] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {rec.explanation}
                  </p>
                  {rec.url && (
                    <a href={addUtmParams(rec.url, rec.product_name)} target="_blank" rel="noopener noreferrer" onClick={() => trackAction("shop_now_click", rec.product_name, rec.url)}>
                      <Button
                        data-testid={`compare-shop-btn-${i}`}
                        className="w-full bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        Shop Now <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
